import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useMutation } from 'react-apollo'
import classNames from 'classnames'

import {
  FORM_MESSAGES,
  FORM_VALUES
} from '../../../../constants'
import { getGQLErrorMsg } from '../../../../lib/apollo'
import { useMutateAndLoading } from '../../../../hooks/use-mutate-and-loading'
import Form from '../../../forms/layout-form'
import Row from '../../../forms/layout-row'
import Col from '../../../forms/layout-column'
import PasswordInput from '../../../forms/password-input'
import Button from '../../../button'
import { CardWrapper, CardContent, CardInnerHeading } from '../../../shared/settings/card'
import EditButton from '../../../shared/settings/edit-button'
import UPDATE_PASSWORD_MUTATION from '../../../../../graphql/mutations/change-password.gql'

import styles from './change-password-form.css'

export default function ChangePasswordForm() {
  const [isEditable, setIsEditable] = useState(false)
  const [formError, setFormError] = useState('')
  const [updatePassword] = useMutation(UPDATE_PASSWORD_MUTATION)
  const mutateAndLoading = useMutateAndLoading(updatePassword)
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: {
      errors,
      touchedFields,
      isSubmitted,
      isSubmitting
    }
  } = useForm({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      newPasswordConfirmation: ''
    },
    mode: 'onChange'
  })

  const onSubmit = async values => {
    setFormError('')

    try {
      await mutateAndLoading('changePassword', {
        variables: {
          oldPassword: values.oldPassword,
          newPassword: values.newPassword
        }
      })
      reset()
      setIsEditable(false)
    } catch (error) {
      setFormError(getGQLErrorMsg(error))
    }
  }

  const cancelEdit = (event) => {
    event.preventDefault()
    reset()
    setFormError('')
    setIsEditable(false)
  }

  const showWarning = (field, value) => (value || touchedFields[field] || isSubmitted) &&
    errors[field]?.message

  return (
    <CardWrapper>
      <CardContent className={styles.cardContent}>
        <CardInnerHeading className={styles.cardInnerHeading} header="Account password" />
        {!isEditable && (
          <div className={styles.editButton}>
            <EditButton text="Change password" onClick={() => setIsEditable(true)} />
          </div>
        )}
        {isEditable && (
          <Form
            handleSubmit={handleSubmit(onSubmit)}
            isSubmitting={isSubmitting}
            formClassName={styles.formClass}
          >
            <Row>
              <Col>
                <Controller
                  name="oldPassword"
                  control={control}
                  rules={{
                    required: FORM_MESSAGES.required,
                    minLength: {
                      value: FORM_VALUES.password.min,
                      message: FORM_MESSAGES.password.min
                    }
                  }}
                  render={({ field }) => (
                    <PasswordInput
                      wrapperClassName={styles.enterCurrentPw}
                      label="Enter Current Password"
                      warning={showWarning(field.name, field.value)}
                      {...field}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name="newPassword"
                  control={control}
                  rules={{
                    required: FORM_MESSAGES.required,
                    minLength: {
                      value: FORM_VALUES.password.min,
                      message: FORM_MESSAGES.password.min
                    }
                  }}
                  render={({ field }) => (
                    <PasswordInput
                      label="Enter New Password"
                      warning={showWarning(field.name, field.value)}
                      {...field}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Controller
                  name="newPasswordConfirmation"
                  control={control}
                  rules={{
                    required: FORM_MESSAGES.required,
                    validate: value => value === getValues('newPassword') ||
                      FORM_MESSAGES.password.match
                  }}
                  render={({ field }) => (
                    <PasswordInput
                      label="Confirm New Password"
                      warning={showWarning(field.name, field.value)}
                      {...field}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <Button
                  lightBGPrimary
                  className={styles.submitButton}
                  disabled={isSubmitting}
                  text="Save"
                  e2eIdentifier="change-password-save-button"
                />
              </Col>
              <Col>
                <Button
                  secondary
                  className={styles.cancelButton}
                  disabled={isSubmitting}
                  text="Cancel"
                  onClick={cancelEdit}
                  e2eIdentifier="change-password-cancel-button"
                />
              </Col>
            </Row>
            {formError && (
              <Row>
                <Col>
                  <div className={styles.warning}>{formError}</div>
                </Col>
              </Row>
            )}
          </Form>
        )}
        {!isEditable && (
          <Row>
            <Col>
              <PasswordInput
                inputClass={classNames(styles.inputClass)}
                label="Password"
                name="password"
                value=""
                onChange={() => {}}
                onBlur={() => {}}
                readonly={!isEditable}
                readonlyTextOverwrite="****"
                readonlyClassName={styles.pw}
              />
            </Col>
          </Row>
        )}
      </CardContent>
    </CardWrapper>
  )
}
